/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { ConfirmDialog } from "primereact/confirmdialog";
import GestionAccesos from "../../../../elementos/GestionAccesos";
import PermisosAcceso from "../../../../elementos/PermisosAcceso";
import { ROLES_ADMIN } from "../../../../roles";
import {
  accessAPI,
  Boton,
  Titulo,
  CampoInformacion,
  Formulario,
  Sidebar,
  tituloBase,
  usePermiso
} from "../../../../shared";

function nombreCompleto(administrador) {
  return (
    [administrador.nombre, administrador.apellido].filter(Boolean).join(" ") ||
    administrador.email
  );
}

export default function Administrador() {
  const [administrador, setAdministrador] = useState(null);
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [sidebarAccesos, setSidebarAccesos] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [loaderEliminar, setLoaderEliminar] = useState(false);
  const { usuarioId } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  // Con lectura se ven los datos y los accesos, sin poder editar ni eliminar
  const { puedeEditar } = usePermiso("admin.administradores");

  function mostrarError(respuesta, porDefecto) {
    toast.current?.show({
      severity: "error",
      summary: "Error",
      detail: respuesta[0]?.msg || porDefecto,
      sticky: true
    });
  }

  useEffect(() => {
    accessAPI(
      "GET",
      `admin/administrador/${usuarioId}`,
      null,
      (respuesta) => {
        setAdministrador(respuesta);
        document.title = nombreCompleto(respuesta);
      },
      (respuesta) => mostrarError(respuesta, "Error al cargar el administrador")
    );
    return () => (document.title = tituloBase());
  }, [usuarioId]);

  function editarSubmit(camposFormulario) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/administrador/${usuarioId}`,
      camposFormulario,
      (respuesta) => {
        setAdministrador(respuesta);
        document.title = nombreCompleto(respuesta);
        setSidebarEditar(false);
        setLoaderEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Administrador actualizado exitosamente"
        });
      },
      (respuesta) => {
        setLoaderEditar(false);
        mostrarError(respuesta, "Error al actualizar el administrador");
      }
    );
  }

  function eliminar() {
    setLoaderEliminar(true);
    accessAPI(
      "DELETE",
      `admin/administrador/${usuarioId}`,
      null,
      () => navigate("/config/administradores"),
      (respuesta) => {
        setLoaderEliminar(false);
        mostrarError(respuesta, "Error al eliminar el administrador");
      }
    );
  }

  const editarFormConfig = administrador
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "nombre",
                tipoitemformularioid: 1,
                etiqueta: "Nombre",
                requerido: true,
                respuesta: administrador.nombre
              },
              {
                nombre: "apellido",
                tipoitemformularioid: 1,
                etiqueta: "Apellido",
                requerido: true,
                respuesta: administrador.apellido
              },
              {
                nombre: "email",
                tipoitemformularioid: 1,
                etiqueta: "Email",
                requerido: true,
                respuesta: administrador.email
              },
              {
                nombre: "accesotipo",
                tipoitemformularioid: 5,
                etiqueta: "Rol",
                opciones: "admin/administrador/roles",
                requerido: true,
                sinOpcionInicial: true,
                respuesta: administrador.accesotipo
              }
            ]
          }
        ],
        botones: [
          { etiqueta: "Guardar cambios", callback: editarSubmit, validar: true }
        ]
      }
    : null;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />

      {!administrador && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
            <Skeleton width="2rem" height="2rem" shape="circle" />
            <Skeleton width="15rem" height="2rem" />
          </div>
          <Card>
            <Skeleton
              width="6rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <div className="grid">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="col-12 md:col-4">
                  <Skeleton
                    width="50%"
                    height="0.8rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="80%" height="1.2rem" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {administrador && (
        <>
          <Titulo
            urlVolver="/config/administradores"
            botones={
              <>
                <Boton transparente callback={() => setSidebarAccesos(true)}>
                  Gestionar accesos
                </Boton>
                {puedeEditar && (
                  <Boton
                    rojo
                    confirmacion={`¿Eliminar a ${nombreCompleto(administrador)}?`}
                    callback={eliminar}
                    loader={loaderEliminar}
                  >
                    Eliminar
                  </Boton>
                )}
              </>
            }
          >
            {nombreCompleto(administrador)}
          </Titulo>

          <Card>
            <Titulo
              botones={
                puedeEditar && (
                  <Boton icono negro callback={() => setSidebarEditar(true)}>
                    <i className="pi pi-pencil" />
                  </Boton>
                )
              }
            >
              Detalles
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Nombre">
                {administrador.nombre}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Apellido">
                {administrador.apellido}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Email">
                {administrador.email}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Rol">
                {administrador.rol}
              </CampoInformacion>
            </div>
          </Card>

          <Card css={{ marginTop: "1em" }}>
            <PermisosAcceso
              // Al cambiarle el rol cambian los valores por defecto
              key={`${administrador.usuarioAccesoId}-${administrador.accesotipo}`}
              usuarioAccesoId={administrador.usuarioAccesoId}
              toast={toast}
            />
          </Card>

          <Sidebar
            visible={sidebarAccesos}
            position="right"
            onHide={() => setSidebarAccesos(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Gestionar accesos</Titulo>
            {/* Se monta recién al abrir el sidebar para que recargue los
                accesos cada vez. */}
            {sidebarAccesos && (
              <GestionAccesos
                usuarioId={administrador.id}
                toast={toast}
                // Sin acceso de administrador (o sin usuario) esta ficha deja
                // de existir: vuelve al listado.
                onAccesoEliminado={(acceso) => {
                  if (ROLES_ADMIN.includes(acceso.acceso.accesotipo)) {
                    navigate("/config/administradores");
                  }
                }}
                onUsuarioEliminado={() => navigate("/config/administradores")}
              />
            )}
          </Sidebar>

          <Sidebar
            visible={sidebarEditar}
            position="right"
            onHide={() => setSidebarEditar(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Editar administrador</Titulo>
            <Formulario
              key={JSON.stringify(administrador)}
              config={editarFormConfig}
              loader={loaderEditar}
            />
          </Sidebar>
        </>
      )}
    </>
  );
}
